// Pure helpers that derive per-fight round lists and distinct fight pairs
// from raw `unit:combat` events. The React modal consumes these as props
// (separate `*.tsx` file); this module stays React-free so it can be
// exercised under `bun test`.
//
// Spec: docs/02-systems/combat-visualisation.md §1a, §4.

import type { CombatAxialCoord } from './shouldAnimateCombat.js';
import type { SimEvent } from './useCombatEngagements.js';

export interface CombatRound {
  readonly tick: number;
  readonly attackerId: string;
  readonly defenderId: string;
  readonly attackerMoraleAfter: number;
  readonly defenderMoraleAfter: number;
  readonly attackerDamage: number;
  readonly defenderDamage: number;
  readonly defenderKilled: boolean;
  readonly attackerKilled: boolean;
  readonly position: CombatAxialCoord;
}

export interface CombatFightRef {
  readonly attackerId: string;
  readonly defenderId: string;
}

function asNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function asBool(v: unknown): boolean {
  return v === true;
}

function asCoord(v: unknown): CombatAxialCoord | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as { q?: unknown; r?: unknown };
  if (typeof o.q !== 'number' || typeof o.r !== 'number') return null;
  if (!Number.isFinite(o.q) || !Number.isFinite(o.r)) return null;
  return { q: o.q, r: o.r };
}

function toRound(ev: SimEvent): CombatRound | null {
  const payload = ev.payload;
  if (!payload) return null;
  const attackerId = typeof payload.unitId === 'string' ? (payload.unitId as string) : null;
  const defenderId = typeof payload.defenderId === 'string' ? (payload.defenderId as string) : null;
  const attackerMoraleAfter = asNumber(payload.attackerMoraleAfter);
  const defenderMoraleAfter = asNumber(payload.defenderMoraleAfter);
  const attackerDamage = asNumber(payload.attackerDamage) ?? 0;
  const defenderDamage = asNumber(payload.defenderDamage) ?? 0;
  const defenderKilled = asBool(payload.defenderKilled);
  const attackerKilled = asBool(payload.attackerKilled);
  const position = asCoord(payload.to) ?? asCoord(payload.from);
  if (
    !attackerId ||
    !defenderId ||
    attackerMoraleAfter === null ||
    defenderMoraleAfter === null ||
    !position
  ) {
    return null;
  }
  return {
    tick: ev.id,
    attackerId,
    defenderId,
    attackerMoraleAfter,
    defenderMoraleAfter,
    attackerDamage,
    defenderDamage,
    defenderKilled,
    attackerKilled,
    position,
  };
}

/**
 * Rounds that belong to the (attackerId, defenderId) pair, in either
 * direction — counter-strikes count as rounds in the same fight.
 */
export function getCombatRounds(
  events: ReadonlyArray<SimEvent>,
  attackerId: string,
  defenderId: string,
): ReadonlyArray<CombatRound> {
  const out: CombatRound[] = [];
  for (const ev of events) {
    if (ev.type !== 'unit:combat') continue;
    const round = toRound(ev);
    if (!round) continue;
    const sameForward = round.attackerId === attackerId && round.defenderId === defenderId;
    const sameReverse = round.attackerId === defenderId && round.defenderId === attackerId;
    if (!sameForward && !sameReverse) continue;
    out.push(round);
  }
  return out;
}

/** Distinct attacker/defender pairs in event order. */
export function getCombatFights(events: ReadonlyArray<SimEvent>): ReadonlyArray<CombatFightRef> {
  const seen = new Set<string>();
  const out: CombatFightRef[] = [];
  for (const ev of events) {
    if (ev.type !== 'unit:combat') continue;
    const payload = ev.payload;
    if (!payload) continue;
    const attackerId = typeof payload.unitId === 'string' ? (payload.unitId as string) : null;
    const defenderId =
      typeof payload.defenderId === 'string' ? (payload.defenderId as string) : null;
    if (!attackerId || !defenderId) continue;
    const key = `${attackerId}::${defenderId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ attackerId, defenderId });
  }
  return out;
}
